import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, allVideos, articles } from '@/mocks/data';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('q') || '';
  const [activeTab, setActiveTab] = useState(slug || 'dianying');

  const currentCategory = categories.find((c) => c.slug === activeTab);

  const filteredVideos = allVideos.filter((v) => {
    const matchCat = v.category === activeTab;
    const matchSearch = searchQuery
      ? v.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        v.tags.some((t) => t.includes(searchQuery))
      : true;
    return matchCat && matchSearch;
  });

  const categoryArticles = articles.filter((a) => a.category === activeTab);

  useEffect(() => {
    if (slug) {
      setActiveTab(slug);
    }
  }, [slug]);

  const handleTabChange = useCallback((catSlug: string) => {
    setActiveTab(catSlug);
    window.history.pushState(null, '', `/category/${catSlug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const catTitle = currentCategory?.name || '分类';
  const catDesc = currentCategory?.description || '';
  const catKeywords = currentCategory?.keywords || '';

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${catTitle} - 69视频`,
    description: catDesc,
    url: `https://woaimiao.cn/category/${activeTab}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredVideos.map((v, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'Movie',
          name: v.title,
          url: `https://woaimiao.cn/article/${v.id}`,
          image: v.thumbnail,
          description: v.description,
        },
      })),
    },
  };

  return (
    <>
      <SEOHead
        title={`${catTitle} - 69视频 | ${catDesc}`}
        description={catDesc}
        keywords={catKeywords}
        canonicalUrl={`https://woaimiao.cn/category/${activeTab}`}
        structuredData={collectionSchema}
      />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: catTitle },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-8">
          <div className="max-w-[1280px] mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 font-heading">
                {catTitle}
              </h1>
              <p className="mt-2 text-sm text-foreground-500">{catDesc}</p>
            </div>

            <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-8 flex-nowrap" role="tablist">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  role="tab"
                  aria-selected={activeTab === cat.slug}
                  onClick={() => handleTabChange(cat.slug)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeTab === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </button>
              ))}
            </div>

            {searchQuery && (
              <div className="mb-6 px-4 py-3 bg-primary-50 border border-primary-200 rounded-lg flex items-center gap-2">
                <i className="ri-search-line text-primary-500"></i>
                <span className="text-sm text-primary-700">
                  搜索结果：<strong>{searchQuery}</strong>
                </span>
                <span className="text-xs text-foreground-500 ml-2">
                  共找到 {filteredVideos.length} 个结果
                </span>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {filteredVideos.map((video) => (
                <Link
                  key={video.id}
                  to={`/article/${video.id}`}
                  className="group block cursor-pointer"
                >
                  <div className="relative aspect-video rounded-lg overflow-hidden bg-background-200">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      title={`${video.title} - 69视频`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors"></div>
                    <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/70 text-background-50 text-xs rounded">
                      {video.duration}
                    </div>
                    <div className="absolute top-2 left-2">
                      <span className="px-2 py-0.5 bg-primary-500 text-background-50 text-xs rounded whitespace-nowrap">
                        {video.rating}
                      </span>
                    </div>
                    {video.isHot && (
                      <div className="absolute top-2 right-2">
                        <span className="px-2 py-0.5 bg-accent-500 text-background-50 text-xs rounded whitespace-nowrap">
                          热播
                        </span>
                      </div>
                    )}
                  </div>
                  <h3 className="mt-2.5 text-sm font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-1 font-heading">
                    {video.title}
                  </h3>
                  <p className="mt-1 text-xs text-foreground-500 line-clamp-1">{video.description}</p>
                  <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-500">
                    <span>{video.year}</span>
                    <span>·</span>
                    <span>{video.views}次播放</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                    {video.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 bg-secondary-100 text-secondary-800 text-xs rounded whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </Link>
              ))}
            </div>

            {filteredVideos.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                  <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">暂无相关内容</p>
              </div>
            )}
          </div>
        </section>

        {categoryArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-12 bg-background-100">
            <div className="max-w-[1280px] mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-6">
                相关资讯
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group block bg-background-50 rounded-xl border border-background-200 overflow-hidden hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="aspect-[16/9] overflow-hidden bg-background-200">
                      <img
                        src={article.thumbnail}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2 font-heading">
                        {article.title}
                      </h3>
                      <p className="mt-2 text-xs text-foreground-500 line-clamp-2">{article.summary}</p>
                      <div className="flex items-center gap-3 mt-3 text-xs text-foreground-500">
                        <span><i className="ri-eye-line mr-1"></i>{article.views}</span>
                        <span>{article.date}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}